/**
 * 版本策略工具 - 分层架构设计
 *
 * @description
 * 提供清晰、分层的版本和平台控制方案，支持在不同版本（WOA/Internal/Pro/Sgp）
 * 和不同平台（Android/iOS/Web等）之间优雅地处理差异化逻辑。
 *
 * @architecture 架构层次
 * ┌─────────────────────────────────────────────────┐
 * │              第一层：类型定义层                    │
 * │  VersionStrategy, VersionGroup, VersionMeta      │
 * │  PlatformStrategy, PlatformGroup, PlatformMeta   │
 * └─────────────────────────────────────────────────┘
 *                      ↓
 * ┌─────────────────────────────────────────────────┐
 * │            第二层：信息查询层                      │
 * │  VersionInfo, PlatformInfo                      │
 * │  (is, inGroup, matchAny, matchAll)              │
 * └─────────────────────────────────────────────────┘
 *                      ↓
 * ┌─────────────────────────────────────────────────┐
 * │             第三层：策略执行层                     │
 * │  VersionStrategyExecutor, EnvStrategyExecutor   │
 * │  (execute, getValue, when, whenGroup, switch)   │
 * └─────────────────────────────────────────────────┘
 *                      ↓
 * ┌─────────────────────────────────────────────────┐
 * │              第四层：便捷工具层                    │
 * │  versionStrategy, platformInfo, envStrategy     │
 * │  Hooks, 简化函数                                 │
 * └─────────────────────────────────────────────────┘
 *
 * @features 核心特性
 * 1. 版本分组：Internal(内部), External(外部), Domestic(国内), International(国际)
 * 2. 平台分组：Mobile(移动端), Desktop(桌面端), Web(Web端), CrossPlatform(跨平台)
 * 3. 组合判断：同时支持版本和平台的组合条件判断
 * 4. 多种使用方式：策略模式、Switch-Case、链式调用、React Hook
 * 5. 类型安全：完整的 TypeScript 类型支持
 *
 * @example 快速开始
 * ```typescript
 * // 1. 版本判断
 * if (versionInfo.inGroup(VersionGroup.Internal)) {
 *   // 内部版本逻辑
 * }
 *
 * // 2. 平台判断
 * if (platformInfo.inGroup(PlatformGroup.Mobile)) {
 *   // 移动端逻辑
 * }
 *
 * // 3. 组合判断（版本 + 平台）
 * if (versionInfo.is(MultiApp.WOA) && !platformInfo.is(CapitalPlatform.Hippy, CapitalPlatform.Kuikly)) {
 *   // WOA 版本且非 Hippy/Kuikly 平台
 * }
 *
 * // 4. 环境策略（推荐）
 * const shouldShow = envStrategy.check({
 *   versions: [MultiApp.WOA],
 *   excludePlatforms: [CapitalPlatform.Hippy, CapitalPlatform.Kuikly, CapitalPlatform.MiniProgram],
 * });
 * ```
 *
 * @see version-strategy.example.ts 查看更多使用示例
 */

import { MultiApp } from '@/config/context';
import { getCapitalPlatform } from '@/infrastructure/product-info';
import { CapitalPlatform } from '@/infrastructure/utils/app';

// ============================================
// 第一层：类型定义层
// ============================================

/**
 * 平台类型（使用已有的 CapitalPlatform 枚举）
 */
export type PlatformType = CapitalPlatform;

/**
 * 版本策略配置类型
 */
export type VersionStrategy<T = any> = {
  [key in MultiApp]?: T;
} & {
  default?: T; // 默认策略，当没有匹配到特定版本时使用
};

/**
 * 平台策略配置类型
 */
export type PlatformStrategy<T = any> = {
  [key in PlatformType]?: T;
} & {
  default?: T;
};

/**
 * 版本分组定义
 */
export enum VersionGroup {
  /** 内部版本组 (WOA + Internal) */
  Internal = 'internal',
  /** 外部版本组 (Pro + Sgp) */
  External = 'external',
  /** 国内版本组 (WOA + Internal + Pro) */
  Domestic = 'domestic',
  /** 国际版本组 (Sgp) */
  International = 'international',
}

/**
 * 平台分组定义
 */
export enum PlatformGroup {
  /** 移动端平台 (Android + IOS) */
  Mobile = 'mobile',
  /** 桌面端平台 (Mac + Windows + Linux) */
  Desktop = 'desktop',
  /** Web/跨平台框架 (Flutter + Hippy + Kuikly + ReactNative) */
  CrossPlatform = 'cross-platform',
  /** 小程序/Electron */
  Embedded = 'embedded',
  /** Apple 生态 (IOS + Mac + VisionOS + AppleTV) */
  Apple = 'apple',
}

/**
 * 版本分组映射（预留，未来可能用于批量操作）
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// const VERSION_GROUP_MAP: Record<VersionGroup, MultiApp[]> = {
//   [VersionGroup.Internal]: [MultiApp.WOA, MultiApp.Internal],
//   [VersionGroup.External]: [MultiApp.Pro, MultiApp.Sgp],
//   [VersionGroup.Domestic]: [MultiApp.WOA, MultiApp.Internal, MultiApp.Pro],
//   [VersionGroup.International]: [MultiApp.Sgp],
// };

/**
 * 平台分组映射
 */
const PLATFORM_GROUP_MAP: Record<PlatformGroup, CapitalPlatform[]> = {
  [PlatformGroup.Mobile]: [CapitalPlatform.Android, CapitalPlatform.IOS],
  [PlatformGroup.Desktop]: [CapitalPlatform.Mac, CapitalPlatform.Windows, CapitalPlatform.Linux],
  [PlatformGroup.CrossPlatform]: [
    CapitalPlatform.Flutter,
    CapitalPlatform.Hippy,
    CapitalPlatform.Kuikly,
    CapitalPlatform.ReactNative,
  ],
  [PlatformGroup.Embedded]: [CapitalPlatform.MiniProgram, CapitalPlatform.Electron],
  [PlatformGroup.Apple]: [
    CapitalPlatform.IOS,
    CapitalPlatform.Mac,
    CapitalPlatform.VisionOS,
    CapitalPlatform.AppleTV,
  ],
};

/**
 * 版本元数据
 */
interface VersionMeta {
  /** 版本名称 */
  name: string;
  /** 版本分组 */
  groups: VersionGroup[];
  /** 是否内部版本 */
  isInternal: boolean;
  /** 是否外部版本 */
  isExternal: boolean;
}

/**
 * 平台元数据
 */
interface PlatformMeta {
  /** 平台名称 */
  name: string;
  /** 平台分组 */
  groups: PlatformGroup[];
  /** 是否移动端 */
  isMobile: boolean;
  /** 是否桌面端 */
  isDesktop: boolean;
  /** 是否跨平台框架 */
  isCrossPlatform: boolean;
}

/**
 * 版本元数据映射
 */
const VERSION_META_MAP: Record<MultiApp, VersionMeta> = {
  [MultiApp.WOA]: {
    name: 'WOA',
    groups: [VersionGroup.Internal, VersionGroup.Domestic],
    isInternal: true,
    isExternal: false,
  },
  [MultiApp.Internal]: {
    name: 'Internal',
    groups: [VersionGroup.Internal, VersionGroup.Domestic],
    isInternal: true,
    isExternal: false,
  },
  [MultiApp.Pro]: {
    name: 'Pro',
    groups: [VersionGroup.External, VersionGroup.Domestic],
    isInternal: false,
    isExternal: true,
  },
  [MultiApp.Sgp]: {
    name: 'SGP',
    groups: [VersionGroup.External, VersionGroup.International],
    isInternal: false,
    isExternal: true,
  },
};

/**
 * 平台元数据映射
 */
const PLATFORM_META_MAP: Record<CapitalPlatform, PlatformMeta> = {
  [CapitalPlatform.Android]: {
    name: 'Android',
    groups: [PlatformGroup.Mobile],
    isMobile: true,
    isDesktop: false,
    isCrossPlatform: false,
  },
  [CapitalPlatform.IOS]: {
    name: 'iOS',
    groups: [PlatformGroup.Mobile, PlatformGroup.Apple],
    isMobile: true,
    isDesktop: false,
    isCrossPlatform: false,
  },
  [CapitalPlatform.Mac]: {
    name: 'macOS',
    groups: [PlatformGroup.Desktop, PlatformGroup.Apple],
    isMobile: false,
    isDesktop: true,
    isCrossPlatform: false,
  },
  [CapitalPlatform.Windows]: {
    name: 'Windows',
    groups: [PlatformGroup.Desktop],
    isMobile: false,
    isDesktop: true,
    isCrossPlatform: false,
  },
  [CapitalPlatform.Linux]: {
    name: 'Linux',
    groups: [PlatformGroup.Desktop],
    isMobile: false,
    isDesktop: true,
    isCrossPlatform: false,
  },
  [CapitalPlatform.Flutter]: {
    name: 'Flutter',
    groups: [PlatformGroup.CrossPlatform],
    isMobile: false,
    isDesktop: false,
    isCrossPlatform: true,
  },
  [CapitalPlatform.HarmonyOS]: {
    name: 'HarmonyOS',
    groups: [PlatformGroup.Mobile],
    isMobile: true,
    isDesktop: false,
    isCrossPlatform: false,
  },
  [CapitalPlatform.Hippy]: {
    name: 'Hippy',
    groups: [PlatformGroup.CrossPlatform],
    isMobile: false,
    isDesktop: false,
    isCrossPlatform: true,
  },
  [CapitalPlatform.VisionOS]: {
    name: 'VisionOS',
    groups: [PlatformGroup.Apple],
    isMobile: false,
    isDesktop: false,
    isCrossPlatform: false,
  },
  [CapitalPlatform.Kuikly]: {
    name: 'Kuikly',
    groups: [PlatformGroup.CrossPlatform],
    isMobile: false,
    isDesktop: false,
    isCrossPlatform: true,
  },
  [CapitalPlatform.MiniProgram]: {
    name: 'MiniProgram',
    groups: [PlatformGroup.Embedded],
    isMobile: false,
    isDesktop: false,
    isCrossPlatform: false,
  },
  [CapitalPlatform.Electron]: {
    name: 'Electron',
    groups: [PlatformGroup.Embedded],
    isMobile: false,
    isDesktop: false,
    isCrossPlatform: true,
  },
  [CapitalPlatform.AppleTV]: {
    name: 'Apple TV',
    groups: [PlatformGroup.Apple],
    isMobile: false,
    isDesktop: false,
    isCrossPlatform: false,
  },
  [CapitalPlatform.ReactNative]: {
    name: 'React Native',
    groups: [PlatformGroup.CrossPlatform],
    isMobile: false,
    isDesktop: false,
    isCrossPlatform: true,
  },
};

// ============================================
// 第二层：版本信息查询层
// ============================================

/**
 * 版本信息查询器
 */
export class VersionInfo {
  private version: MultiApp;

  constructor(version?: MultiApp) {
    this.version = version || (process.env.APP_VERSION as MultiApp);
  }

  /**
   * 获取当前版本
   */
  get current(): MultiApp {
    return this.version;
  }

  /**
   * 获取版本元数据
   */
  get meta(): VersionMeta {
    return VERSION_META_MAP[this.version];
  }

  /**
   * 是否为指定版本
   */
  is(...versions: MultiApp[]): boolean {
    return versions.includes(this.version);
  }

  /**
   * 是否不是指定版本
   */
  isNot(...versions: MultiApp[]): boolean {
    return !this.is(...versions);
  }

  /**
   * 是否属于指定分组
   */
  inGroup(...groups: VersionGroup[]): boolean {
    return groups.some(group => this.meta.groups.includes(group));
  }

  /**
   * 是否为内部版本
   */
  get isInternal(): boolean {
    return this.meta.isInternal;
  }

  /**
   * 是否为外部版本
   */
  get isExternal(): boolean {
    return this.meta.isExternal;
  }

  /**
   * 匹配多个条件（任意满足即可）
   */
  matchAny(conditions: {
    versions?: MultiApp[];
    groups?: VersionGroup[];
    internal?: boolean;
    external?: boolean;
  }): boolean {
    const checks: boolean[] = [];

    if (conditions.versions) {
      checks.push(this.is(...conditions.versions));
    }
    if (conditions.groups) {
      checks.push(this.inGroup(...conditions.groups));
    }
    if (conditions.internal !== undefined) {
      checks.push(this.isInternal === conditions.internal);
    }
    if (conditions.external !== undefined) {
      checks.push(this.isExternal === conditions.external);
    }

    return checks.some(check => check === true);
  }

  /**
   * 匹配多个条件（全部满足）
   */
  matchAll(conditions: {
    versions?: MultiApp[];
    groups?: VersionGroup[];
    internal?: boolean;
    external?: boolean;
  }): boolean {
    const checks: boolean[] = [];

    if (conditions.versions) {
      checks.push(this.is(...conditions.versions));
    }
    if (conditions.groups) {
      checks.push(this.inGroup(...conditions.groups));
    }
    if (conditions.internal !== undefined) {
      checks.push(this.isInternal === conditions.internal);
    }
    if (conditions.external !== undefined) {
      checks.push(this.isExternal === conditions.external);
    }

    return checks.every(check => check === true);
  }
}

/**
 * 平台信息查询器
 */
export class PlatformInfo {
  private staticPlatform?: PlatformType;

  constructor(platform?: PlatformType) {
    // 如果传入了平台参数，则使用静态平台；否则动态获取
    this.staticPlatform = platform;
  }

  /**
   * 获取当前平台（动态获取，支持产品切换）
   */
  get current(): PlatformType | undefined {
    return this.staticPlatform ?? (getCapitalPlatform() as PlatformType);
  }

  /**
   * 获取平台元数据
   */
  get meta(): PlatformMeta | undefined {
    const platform = this.current;
    return platform ? PLATFORM_META_MAP[platform] : undefined;
  }

  /**
   * 是否为指定平台
   */
  is(...platforms: PlatformType[]): boolean {
    const platform = this.current;
    return platform ? platforms.includes(platform) : false;
  }

  /**
   * 是否不是指定平台
   */
  isNot(...platforms: PlatformType[]): boolean {
    return !this.is(...platforms);
  }

  /**
   * 是否属于指定平台分组
   */
  inGroup(...groups: PlatformGroup[]): boolean {
    const platform = this.current;
    if (!platform || !this.meta) return false;
    return groups.some(group => PLATFORM_GROUP_MAP[group]?.includes(platform));
  }

  /**
   * 是否为移动端
   */
  get isMobile(): boolean {
    return this.meta?.isMobile || false;
  }

  /**
   * 是否为桌面端
   */
  get isDesktop(): boolean {
    return this.meta?.isDesktop || false;
  }

  /**
   * 是否为跨平台框架
   */
  get isCrossPlatform(): boolean {
    return this.meta?.isCrossPlatform || false;
  }

  /**
   * 匹配多个条件（任意满足即可）
   */
  matchAny(conditions: {
    platforms?: PlatformType[];
    groups?: PlatformGroup[];
    mobile?: boolean;
    desktop?: boolean;
  }): boolean {
    const checks: boolean[] = [];

    if (conditions.platforms) {
      checks.push(this.is(...conditions.platforms));
    }
    if (conditions.groups) {
      checks.push(this.inGroup(...conditions.groups));
    }
    if (conditions.mobile !== undefined) {
      checks.push(this.isMobile === conditions.mobile);
    }
    if (conditions.desktop !== undefined) {
      checks.push(this.isDesktop === conditions.desktop);
    }

    return checks.some(check => check === true);
  }

  /**
   * 匹配多个条件（全部满足）
   */
  matchAll(conditions: {
    platforms?: PlatformType[];
    groups?: PlatformGroup[];
    mobile?: boolean;
    desktop?: boolean;
  }): boolean {
    const checks: boolean[] = [];

    if (conditions.platforms) {
      checks.push(this.is(...conditions.platforms));
    }
    if (conditions.groups) {
      checks.push(this.inGroup(...conditions.groups));
    }
    if (conditions.mobile !== undefined) {
      checks.push(this.isMobile === conditions.mobile);
    }
    if (conditions.desktop !== undefined) {
      checks.push(this.isDesktop === conditions.desktop);
    }

    return checks.every(check => check === true);
  }
}

// ============================================
// 第三层：策略执行层
// ============================================

/**
 * 版本策略执行器
 */
export class VersionStrategyExecutor {
  private versionInfo: VersionInfo;

  constructor(version?: MultiApp) {
    this.versionInfo = new VersionInfo(version);
  }

  /**
   * 获取版本信息
   */
  get info(): VersionInfo {
    return this.versionInfo;
  }

  /**
   * 执行版本策略函数
   * @param strategies 版本策略配置对象
   * @param args 传递给策略函数的参数
   * @returns 策略函数的返回值
   */
  execute<T = any, Args extends any[] = any[]>(
    strategies: VersionStrategy<(...args: Args) => T>,
    ...args: Args
  ): T | undefined {
    const strategy = strategies[this.versionInfo.current] || strategies.default;

    if (!strategy) {
      console.warn(`No strategy found for version: ${this.versionInfo.current}`);
      return undefined;
    }

    return strategy(...args);
  }

  /**
   * 获取版本策略值（非函数类型）
   * @param strategies 版本策略配置对象
   * @returns 对应版本的值
   */
  getValue<T = any>(strategies: VersionStrategy<T>): T | undefined {
    return strategies[this.versionInfo.current] ?? strategies.default;
  }

  /**
   * 条件执行：仅在匹配指定版本时执行
   * @param versions 版本数组
   * @param callback 回调函数
   */
  when(versions: MultiApp[], callback: () => void): this {
    if (this.versionInfo.is(...versions)) {
      callback();
    }
    return this;
  }

  /**
   * 条件执行：仅在匹配指定分组时执行
   * @param groups 分组数组
   * @param callback 回调函数
   */
  whenGroup(groups: VersionGroup[], callback: () => void): this {
    if (this.versionInfo.inGroup(...groups)) {
      callback();
    }
    return this;
  }

  /**
   * 条件执行：仅在不匹配指定版本时执行
   * @param versions 版本数组
   * @param callback 回调函数
   */
  whenNot(versions: MultiApp[], callback: () => void): this {
    if (this.versionInfo.isNot(...versions)) {
      callback();
    }
    return this;
  }

  /**
   * 链式条件执行：switch-case 风格
   */
  switch<T = any>(): VersionSwitch<T> {
    return new VersionSwitch<T>(this.versionInfo);
  }
}

/**
 * Switch 风格的版本策略执行器
 */
class VersionSwitch<T> {
  private versionInfo: VersionInfo;
  private result: T | undefined;
  private matched = false;

  constructor(versionInfo: VersionInfo) {
    this.versionInfo = versionInfo;
  }

  /**
   * case 分支：匹配指定版本
   */
  case(versions: MultiApp[], value: T | (() => T)): this {
    if (!this.matched && this.versionInfo.is(...versions)) {
      this.result = typeof value === 'function' ? (value as () => T)() : value;
      this.matched = true;
    }
    return this;
  }

  /**
   * case 分支：匹配指定分组
   */
  caseGroup(groups: VersionGroup[], value: T | (() => T)): this {
    if (!this.matched && this.versionInfo.inGroup(...groups)) {
      this.result = typeof value === 'function' ? (value as () => T)() : value;
      this.matched = true;
    }
    return this;
  }

  /**
   * default 分支
   */
  default(value: T | (() => T)): T | undefined {
    if (!this.matched) {
      this.result = typeof value === 'function' ? (value as () => T)() : value;
    }
    return this.result;
  }

  /**
   * 获取结果
   */
  end(): T | undefined {
    return this.result;
  }
}

// ============================================
// 第四层：便捷工具层
// ============================================

/**
 * 创建版本策略执行器实例
 */
export const createVersionStrategy = (version?: MultiApp) => new VersionStrategyExecutor(version);

/**
 * 默认的版本策略执行器（使用当前环境版本）
 */
export const versionStrategy = new VersionStrategyExecutor();

/**
 * 默认的版本信息查询器
 */
export const versionInfo = new VersionInfo();

/**
 * 默认的平台信息查询器
 */
export const platformInfo = new PlatformInfo();

/**
 * 环境条件配置（版本 + 平台组合）
 */
export interface EnvConditions {
  /** 允许的版本列表 */
  versions?: MultiApp[];
  /** 排除的版本列表 */
  excludeVersions?: MultiApp[];
  /** 允许的版本分组 */
  versionGroups?: VersionGroup[];
  /** 允许的平台列表 */
  platforms?: PlatformType[];
  /** 排除的平台列表 */
  excludePlatforms?: PlatformType[];
  /** 允许的平台分组 */
  platformGroups?: PlatformGroup[];
}

/**
 * 环境策略执行器（版本 + 平台组合判断）
 */
export class EnvStrategyExecutor {
  private versionInfo: VersionInfo;
  private staticPlatform?: PlatformType;

  constructor(version?: MultiApp, platform?: PlatformType) {
    this.versionInfo = new VersionInfo(version);
    this.staticPlatform = platform;
  }

  /**
   * 检查是否满足环境条件
   */
  check(conditions: EnvConditions): boolean {
    const checks: boolean[] = [];
    // 每次检查都动态创建 PlatformInfo 实例，确保获取最新的平台信息
    const platformInfo = new PlatformInfo(this.staticPlatform);

    // 版本条件
    if (conditions.versions) {
      checks.push(this.versionInfo.is(...conditions.versions));
    }
    if (conditions.excludeVersions) {
      checks.push(this.versionInfo.isNot(...conditions.excludeVersions));
    }
    if (conditions.versionGroups) {
      checks.push(this.versionInfo.inGroup(...conditions.versionGroups));
    }

    // 平台条件
    if (conditions.platforms) {
      checks.push(platformInfo.is(...conditions.platforms));
    }
    if (conditions.excludePlatforms) {
      checks.push(platformInfo.isNot(...conditions.excludePlatforms));
    }
    if (conditions.platformGroups) {
      checks.push(platformInfo.inGroup(...conditions.platformGroups));
    }

    // 所有条件都要满足
    return checks.length > 0 ? checks.every(check => check === true) : true;
  }

  /**
   * 获取版本信息
   */
  get version(): VersionInfo {
    return this.versionInfo;
  }

  /**
   * 获取平台信息（动态获取，支持产品切换）
   */
  get platform(): PlatformInfo {
    return new PlatformInfo(this.staticPlatform);
  }
}

/**
 * 默认的环境策略执行器
 */
export const envStrategy = new EnvStrategyExecutor();

/**
 * 版本策略 Hook（用于 React 组件）
 */
export const useVersionStrategy = () => {
  const currentVersion = process.env.APP_VERSION as MultiApp;
  const info = new VersionInfo(currentVersion);

  return {
    /** 版本信息 */
    info,

    /** 执行策略函数 */
    execute: <T = any, Args extends any[] = any[]>(
      strategies: VersionStrategy<(...args: Args) => T>,
      ...args: Args
    ): T | undefined => {
      const strategy = strategies[currentVersion] || strategies.default;
      return strategy ? strategy(...args) : undefined;
    },

    /** 获取策略值 */
    getValue: <T = any>(strategies: VersionStrategy<T>): T | undefined => (
      strategies[currentVersion] ?? strategies.default
    ),

    /** 是否匹配版本 */
    isVersion: (...versions: MultiApp[]): boolean => info.is(...versions),

    /** 是否属于分组 */
    inGroup: (...groups: VersionGroup[]): boolean => info.inGroup(...groups),

    /** 当前版本 */
    currentVersion,

    /** Switch 风格执行 */
    switch: <T = any>() => new VersionSwitch<T>(info),
  };
};

/**
 * 环境策略 Hook（版本 + 平台组合）
 */
export const useEnvStrategy = () => {
  const currentVersion = process.env.APP_VERSION as MultiApp;
  const currentPlatform = getCapitalPlatform() as PlatformType;
  const env = new EnvStrategyExecutor(currentVersion, currentPlatform);

  return {
    /** 检查环境条件 */
    check: (conditions: EnvConditions) => env.check(conditions),

    /** 版本信息 */
    version: env.version,

    /** 平台信息 */
    platform: env.platform,

    /** 当前版本 */
    currentVersion,

    /** 当前平台 */
    currentPlatform,
  };
};

/**
 * 简化版：根据版本执行不同函数
 * @param strategies 版本策略配置
 * @param args 参数
 */
export function executeByVersion<T = any, Args extends any[] = any[]>(
  strategies: VersionStrategy<(...args: Args) => T>,
  ...args: Args
): T | undefined {
  return versionStrategy.execute(strategies, ...args);
}

/**
 * 简化版：根据版本获取不同的值
 * @param strategies 版本策略配置
 */
export function getValueByVersion<T = any>(strategies: VersionStrategy<T>): T | undefined {
  return versionStrategy.getValue(strategies);
}

/**
 * 简化版：检查是否为指定版本
 */
export function isVersion(...versions: MultiApp[]): boolean {
  return versionInfo.is(...versions);
}

/**
 * 简化版：检查是否属于指定分组
 */
export function inGroup(...groups: VersionGroup[]): boolean {
  return versionInfo.inGroup(...groups);
}

/**
 * 简化版：检查是否为指定平台
 */
export function isPlatform(...platforms: PlatformType[]): boolean {
  return platformInfo.is(...platforms);
}

/**
 * 简化版：检查是否属于指定平台分组
 */
export function inPlatformGroup(...groups: PlatformGroup[]): boolean {
  return platformInfo.inGroup(...groups);
}

/**
 * 简化版：检查环境条件（版本 + 平台组合）
 */
export function checkEnv(conditions: EnvConditions): boolean {
  return envStrategy.check(conditions);
}
